import React, { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { locales, setLocale, getLocale } from '../../../config/locale';
import styles from './TranslationsDropdown.mod.scss';

const LanguageIcon = ({ color = 'currentColor', size = 24, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 1024 1024"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={{ verticalAlign: 'middle', fill: color, overflow: 'hidden' }}
  >
    <path
      d="M657.739 298.976C630.552 239.57 592.047 185.646 544 140.18v178.837c38.96-2.403 77.063-9.18 113.739-20.04zM544 480h159.013c-2.575-41.63-10.15-82.234-22.302-121.102-43.598 13.335-89.394 21.637-136.711 24.228V480z m0 64v96.874c47.317 2.59 93.113 10.893 136.711 24.228 12.152-38.868 19.727-79.472 22.302-121.102H544z m0 339.82c48.047-45.466 86.552-99.389 113.739-158.796-36.676-10.86-74.779-17.637-113.739-20.04v178.837z m-64-564.804V140.179c-48.047 45.467-86.552 99.39-113.739 158.797 36.676 10.86 74.779 17.637 113.739 20.04zM320.987 480H480v-96.874c-47.317-2.59-93.113-10.893-136.711-24.228-12.152 38.868-19.727 79.472-22.302 121.102z m22.302 185.102c43.598-13.335 89.394-21.637 136.711-24.228V544H320.987c2.575 41.63 10.15 82.234 22.302 121.102zM480 883.821V704.984c-38.96 2.403-77.063 9.18-113.739 20.04C393.448 784.43 431.953 838.354 480 883.82zM512 64c150.96 0 284.48 74.666 365.647 189.082C929.512 326.193 960 415.536 960 512s-30.488 185.807-82.353 258.919l-0.237-0.195 0.237 0.195C796.48 885.334 662.96 960 512 960c-150.96 0-284.48-74.666-365.647-189.082C94.488 697.807 64 608.464 64 512s30.488-185.807 82.353-258.918C227.52 138.666 361.04 64 512 64z m-127.917 85.828c-54.456 19.211-103.888 50.532-144.678 91.674a509.327 509.327 0 0 0 66.6 35.402c20.522-45.844 46.86-88.516 78.078-127.076z m333.912 127.076a509.327 509.327 0 0 0 66.6-35.402c-40.79-41.142-90.222-72.463-144.678-91.674 31.217 38.56 57.556 81.232 78.078 127.076zM767.126 480h127.56c-5.778-70.03-30.347-134.733-68.654-189.053a574.601 574.601 0 0 1-85.179 45.798c14.533 45.551 23.552 93.564 26.273 143.255z m-26.273 207.255a574.605 574.605 0 0 1 85.179 45.798c38.307-54.32 62.876-119.022 68.654-189.053h-127.56c-2.72 49.69-11.74 97.704-26.273 143.255zM639.917 874.172c54.456-19.211 103.888-50.532 144.678-91.674a509.326 509.326 0 0 0-66.6-35.402c-20.522 45.844-46.86 88.516-78.078 127.076zM306.005 747.096a509.327 509.327 0 0 0-66.6 35.402c40.79 41.142 90.222 72.463 144.678 91.674-31.217-38.56-57.556-81.232-78.078-127.076zM256.874 544h-127.56c5.778 70.03 30.347 134.733 68.654 189.053a574.601 574.601 0 0 1 85.179-45.798c-14.533-45.551-23.552-93.564-26.273-143.255z m-58.906-253.053c-38.307 54.32-62.876 119.022-68.654 189.053H257l26-143-85.032-46.053z"
      fill={color}
    />
  </svg>
);

const TranslationsDropdown = ({
  iconColor = 'rgba(0, 0, 0, 0.54)',
  menuBackground = '#ffffff',
  textColor = '#1f1f1f',
  activeColor = '#0d47a1',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedLocale(getLocale().locale);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelectLocale = useCallback(
    async (newLocale) => {
      if (selectedLocale === locales[newLocale].locale) {
        return;
      }

      try {
        console.log('Selected locale:', newLocale);
        setLocale(newLocale);
        setSelectedLocale(locales[newLocale].locale);
        setIsOpen(false);
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } catch (error) {
        console.error('Error changing locale:', error);
      }
    },
    [selectedLocale]
  );

  const currentLocale = locales[Object.keys(locales).find((key) => locales[key].locale === selectedLocale)];

  const dropdownStyles = {
    backgroundColor: menuBackground,
  };

  const itemStyles = {
    color: textColor,
  };

  const selectedStyles = {
    color: activeColor,
  };

  return (
    <div className={clsx(styles.container, className)} ref={dropdownRef}>
      <div className={clsx(styles.dropdown, { [styles.dropdownOpen]: isOpen })} style={dropdownStyles}>
        {Object.keys(locales).map((localeKey) => {
          const locale = locales[localeKey];
          const isSelected = selectedLocale === locale.locale;

          return (
            <button
              key={`locale-${locale.locale}`}
              type="button"
              className={clsx(styles.dropdownItem, { [styles.selected]: isSelected })}
              style={{
                ...itemStyles,
                ...(isSelected ? selectedStyles : {}),
              }}
              onClick={() => handleSelectLocale(localeKey)}
              disabled={isSelected}
              aria-selected={isSelected}
            >
              <span className={styles.localeName}>{locale.name}</span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className={styles.trigger}
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <LanguageIcon color={iconColor} size={24} />
      </button>
    </div>
  );
};

export default TranslationsDropdown;
